import React, { useEffect, useState } from 'react';
import { Star, Send } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { getProducts, submitReview } from '../lib/api';

export const ReviewForm = ({ defaultProductId = '', defaultProductName = '', lockProduct = false, onSubmitted }) => {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [name, setName] = useState('');
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState('');
  const [productId, setProductId] = useState(defaultProductId);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!lockProduct) getProducts().then((all) => setProducts(all || [])).catch(() => {});
  }, [lockProduct]);

  const submit = async () => {
    if (!name.trim() || !body.trim()) {
      toast({ title: 'Please add your name and review', variant: 'destructive' });
      return;
    }
    const selected = products.find((p) => p.id === productId);
    const productName = lockProduct ? defaultProductName : (selected ? selected.name : '');
    setSubmitting(true);
    try {
      const res = await submitReview({ name: name.trim(), rating, body: body.trim(), productId, productName });
      toast({ title: 'Review posted', description: res?.message || 'Thanks! Your review is now live.' });
      setName(''); setBody(''); setRating(5);
      if (!lockProduct) setProductId('');
      onSubmitted && onSubmitted();
    } catch (e) {
      toast({ title: 'Could not submit', description: e?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="border border-alp-line rounded-lg bg-alp-card/40 p-6" data-testid="review-form">
      <h3 className="font-serif text-xl text-alp-cream mb-1">Write a review</h3>
      <p className="text-alp-gray text-xs mb-5">Your review goes live right away.</p>

      <div className="mb-4">
        <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Your rating</label>
        <div className="flex items-center gap-1.5" data-testid="review-form-rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onClick={() => setRating(n)} className="p-0.5">
              <Star size={24} className={n <= rating ? 'text-alp-gold fill-alp-gold' : 'text-alp-line hover:text-alp-gold/50'} />
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="review-form-name" className="bg-alp-dark border-alp-line text-alp-cream" placeholder="e.g. Sarah M." />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Product (optional)</label>
          {lockProduct ? (
            <Input value={defaultProductName} disabled data-testid="review-form-product-locked" className="bg-alp-dark border-alp-line text-alp-gray" />
          ) : (
            <select value={productId} onChange={(e) => setProductId(e.target.value)} data-testid="review-form-product" className="w-full h-10 rounded-md bg-alp-dark border border-alp-line text-alp-cream text-sm px-3">
              <option value="">General review</option>
              {products.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          )}
        </div>
      </div>

      <div className="mb-5">
        <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Your review</label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} data-testid="review-form-body" className="bg-alp-dark border-alp-line text-alp-cream" placeholder="Share your experience..." />
      </div>

      <button onClick={submit} disabled={submitting} data-testid="review-form-submit" className="btn-glow inline-flex items-center gap-2 bg-alp-gold text-alp-black px-6 py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-60">
        <Send size={14} /> {submitting ? 'Submitting…' : 'Submit Review'}
      </button>
    </div>
  );
};
